import React, { useState, useEffect } from "react";
import { 
  Calculator, Target, GraduationCap, Users, Globe, Clock, AlertCircle, 
  BookOpen, TrendingUp, ChevronRight 
} from "lucide-react";
import { motion } from "framer-motion";
import ContactForm from "../components/ContactForm";
import { Link } from "react-router-dom";

const SATPage = () => {
  const [rwScore, setRWScore] = useState(600);
  const [mathScore, setMathScore] = useState(600);
  const [totalScore, setTotalScore] = useState(1200);

  const updateScore = () => {
    const total = rwScore + mathScore;
    setTotalScore(total);
  };

  useEffect(() => {
    updateScore();
  }, [rwScore, mathScore]);

  const selfSelectCards = [
    { icon: GraduationCap, title: "Test-Required Universities", desc: "A limited but influential group of US universities remain test-required" },
    { icon: Users, title: "Merit Scholarship Applicants", desc: "Competitive scores strengthen merit aid candidacy at public universities" },
    { icon: TrendingUp, title: "International Students", desc: "US colleges + limited programs outside US. Strategic for competitive applications" }
  ];

  const benchmarks = [
    { score: "1200+", meaning: "Solid foundation", unis: "Regional universities, in-state merit aid opportunities" },
    { score: "1300+", meaning: "Competitive range", unis: "Selective public universities, national merit consideration" },
    { score: "1400+", meaning: "Excellent", unis: "Competitive private colleges, strong merit profiles" },
    { score: "1500+", meaning: "Elite", unis: "Test-required institutions, top merit scholarship consideration" }
  ];

  const myths = [
    { text: "SAT required for all US universities", counter: "Limited but influential group test-required. Most are test-optional" },
    { text: "Test-optional = test-blind", counter: "Test-optional: scores seen if submitted. Test-blind: scores ignored (very rare)" },
    { text: "High SAT compensates for weak GPA", counter: "Admissions holistic: GPA primary. SAT rarely overcomes grades" },
    { text: "Digital SAT is harder", counter: "Same difficulty curve, shorter format, adaptive modules maintain equivalence" },
    { text: "Only Math matters for STEM", counter: "Holistic review: GPA > SAT > essays. RW tests evidence-based reading" }
  ];

  const testFormat = [
    { section: "Reading & Writing", duration: "64 min (adaptive)" },
    { section: "Math", duration: "70 min (adaptive, calculator allowed)" }
  ];

  const preparationFlow = [
    { step: "Diagnostic Assessment", desc: "Full digital SAT with adaptive module simulation + percentile ranking" },
    { step: "Section Gap Analysis", desc: "Math subskills (algebra, advanced math) vs RW (grammar, reading)" },
    { step: "Adaptive Practice Strategy", desc: "First module mastery → second module difficulty optimization" },
    { step: "Proctored Simulations", desc: "Full 2h14m digital tests with College Board score preview format" }
  ];

  const getPercentileBand = (score) => {
    if (score >= 1500) return "Top 5%";
    if (score >= 1400) return "Top 10%";
    if (score >= 1300) return "Top 25%";
    if (score >= 1200) return "Top 40%";
    return "Below average";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      {/* 1. Hero + Calculator */}
      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-light text-slate-900 leading-tight">
              Digital SAT Overview
            </h1>
            <p className="text-xl text-slate-600 max-w-lg leading-relaxed">
              College Board's adaptive test for US undergraduate admissions. Strategic for test-required schools and merit aid.
            </p>
            <p className="text-lg text-slate-500">
              Digital format (2024+). 2 hours 14 minutes. Scored 400-1600. Most US universities test-optional.
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-slate-100 max-h-[32rem] overflow-y-auto"
          >
            <div className="flex items-center gap-3 mb-4">
              <Calculator className="w-6 h-6 text-emerald-600" />
              <h3 className="text-xl font-semibold text-slate-900">Estimate Your Total Score</h3>
            </div>
            <p className="text-xs text-slate-500 mb-6 italic">
              Estimates combined section scores. Digital SAT is adaptive - second module adjusts based on first module performance.
            </p>
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-700">Reading & Writing (200-800)</label>
                <input
                  type="range"
                  min="200" max="800"
                  value={rwScore}
                  onChange={e => setRWScore(+e.target.value)}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500 hover:accent-emerald-600"
                />
                <span className="text-sm font-mono text-slate-600">{rwScore}</span>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-700">Math (200-800)</label>
                <input
                  type="range"
                  min="200" max="800"
                  value={mathScore}
                  onChange={e => setMathScore(+e.target.value)}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500 hover:accent-emerald-600"
                />
                <span className="text-sm font-mono text-slate-600">{mathScore}</span>
              </div>
              <div className="pt-6 border-t border-slate-200">
                <div className="text-3xl font-bold text-slate-900 mb-1">Total: {totalScore}</div>
                <div className="text-sm text-emerald-700 font-semibold">
                  {getPercentileBand(totalScore)} nationally
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Who Takes SAT */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Who Takes the SAT?
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            SAT remains strategic for test-required schools, merit aid, and competitive international applications. Most US universities are test-optional.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {selfSelectCards.map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="group bg-white rounded-3xl p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 border border-slate-100 hover:border-emerald-200"
            >
              <card.icon className="w-12 h-12 text-emerald-600 mb-6 mx-auto" />
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">{card.title}</h3>
              <p className="text-slate-600 leading-relaxed">{card.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3. CTA Cluster */}
      <section className="py-24 px-6 md:px-12 bg-emerald-50 border-t border-emerald-200">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-2xl shadow-lg">
            <BookOpen className="w-8 h-8 text-emerald-600" />
            <div>
              <h3 className="text-xl font-semibold text-slate-900">Next Steps</h3>
              <p className="text-sm text-slate-600">Review preparation pathways for your target percentile</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/test-prep#sat">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-emerald-600 hover:bg-emerald-700 px-8 py-4 rounded-2xl text-lg font-semibold shadow-xl flex items-center gap-2"
            >
              SAT Preparation Programs
              <ChevronRight className="w-5 h-5" />
            </motion.button>
            </Link>
            <Link to="/test-prep">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="border-2 border-slate-600 hover:border-slate-400 bg-white text-slate-900 px-8 py-4 rounded-2xl text-lg font-semibold flex items-center gap-2"
            >
              All Test Prep
            </motion.button>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. Score Benchmarks */}
      <section className="py-24 px-6 md:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            What Your Score Means
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
            Admissions holistic: GPA primary factor. SAT strengthens competitive applications but rarely compensates for grades. Most universities superscore.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {benchmarks.map((bench, i) => (
            <motion.div
              key={bench.score}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-8 hover:shadow-xl border border-slate-100"
            >
              <div className="text-3xl font-bold text-emerald-600 mb-4">{bench.score}</div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3">{bench.meaning}</h4>
              <p className="text-slate-600">{bench.unis}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 5. Global Acceptance */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Institutional Acceptance
          </h2>
          <p className="text-xl text-slate-600">
            Primary use: US undergraduate admissions. Limited international recognition. Merit aid decisions combine SAT with GPA and institutional priorities.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-3xl p-10">
            <Globe className="w-16 h-16 text-emerald-600 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">University Admissions</h3>
            <p className="text-lg text-slate-600">Limited but influential group test-required. Most top universities test-optional.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-10">
            <Target className="w-16 h-16 text-blue-600 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">Merit Scholarships</h3>
            <p className="text-lg text-slate-600">Competitive scores strengthen merit aid candidacy at public universities.</p>
          </motion.div>
        </div>
      </section>

      {/* 6. Common Misconceptions */}
      <section className="py-24 px-6 md:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-12 text-center">
            Common Misconceptions
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {myths.map((myth, i) => (
              <motion.div
                key={myth.text}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="flex flex-col gap-4 p-6 bg-white rounded-2xl border border-slate-200 hover:border-amber-200 group"
              >
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-8 h-8 text-amber-500 flex-shrink-0 mt-1" />
                  <p className="text-slate-700 group-hover:text-slate-900 font-medium">{myth.text}</p>
                </div>
                <p className="text-sm text-slate-600 font-semibold bg-amber-50 p-3 rounded-xl">
                  {myth.counter}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Test Structure */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Test Structure
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-16">
            Total: 2 hours 14 minutes + 10 min break. Fully digital, adaptive testing.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {testFormat.map((format, i) => (
            <motion.div
              key={format.section}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 text-center border border-slate-100 hover:shadow-lg"
            >
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{format.section}</h3>
              <Clock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <div className="text-2xl font-bold text-slate-900">{format.duration}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 8. How We Help You Prepare */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            How We Help You Prepare
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Strategic preparation aligned with digital SAT adaptive algorithms and admissions requirements.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {preparationFlow.map((step, i) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, x: i % 2 ? 20 : -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-10 border border-slate-100 hover:shadow-xl hover:border-emerald-200"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-emerald-700">{i + 1}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{step.step}</h3>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 9. ContactForm */}
      <section id="contact-form" className="max-w-6xl mx-auto px-8 pb-12 bg-slate-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Questions About SAT?
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Schedule consultation: "Do I need SAT? Should I submit my score? What's my best admissions pathway?"
          </p>
        </div>
        <ContactForm />
      </section>
    </div>
  );
};

export default SATPage;
